import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react"
import { useMemo } from "react"
import { FilterProps } from "shared-ts/hooks/useFilter/types/filter.types"
import { newFullIntervalFromTimestampRange } from "shared-ts/libs/dates/interval"

import { FilterButton } from "./FilterButton/FilterButton"
import { FilterHeaderRow } from "./FilterHeaderRow"
import {
  modalPopoverClassName,
  popoverClassName,
  popoverPanelAnchor,
} from "../../../../components/base/overlay/Popover/popoverStyles"
import { MultiCombobox } from "../../../../components/composite/combobox/MultiCombobox"
import { classNames } from "../../../../lib/classNames"
import { IntervalPicker } from "../CalendarPickers/IntervalPicker"
import { FilteredCombobox } from "../Combobox/FilteredCombobox/FilteredCombobox"

export function Filter(props: FilterProps) {
  const { isInModal } = props
  const popoverPanelClassName = classNames(
    isInModal ? modalPopoverClassName : popoverClassName,
    "w-fit"
  )

  return (
    <Popover>
      <PopoverButton as={FilterButton} filter={props.selectedFilter} />
      <PopoverPanel
        anchor={popoverPanelAnchor}
        className={popoverPanelClassName}
        focus
      >
        {({ close }) => <FilterPanel {...props} close={close} />}
      </PopoverPanel>
    </Popover>
  )
}

interface FilterPanelProps extends FilterProps {
  close: () => void
}

function FilterPanel({
  selectedFilter,
  onSelectFilterKey,
  onDeleteFilter,
  filters,
  close,
}: FilterPanelProps) {
  const selectedItemKeys = useMemo<Set<string>>(() => {
    if (!selectedFilter || selectedFilter.type !== "text") {
      return new Set<string>()
    }
    return new Set(selectedFilter.selectedOptions.map((option) => option.key))
  }, [selectedFilter])

  if (!selectedFilter) {
    return (
      <FilteredCombobox
        items={filters}
        keyExtractor={(filter) => filter.key}
        valueExtractor={(filter) => filter.value}
        onSelect={(filter) => onSelectFilterKey(filter.key)}
        comboboxProps={{
          comboboxInputProps: {
            placeholder: "Filter by...",
          },
        }}
      />
    )
  }

  switch (selectedFilter.type) {
    case "text":
      return (
        <div>
          <FilterHeaderRow
            label={selectedFilter.value}
            onClickDelete={() => {
              onDeleteFilter()
              close()
            }}
          />
          <MultiCombobox
            keyExtractor={(option) => option.key}
            items={selectedFilter.options}
            valueExtractor={(option) => option.value}
            contentExtractor={(option) => option.content}
            onSelect={selectedFilter.onSelectOption}
            selectedItemKeys={selectedItemKeys}
            autoFilter={selectedFilter.autoFilterOptions}
            onChangeQuery={selectedFilter.onChangeQuery}
            onLoadMore={selectedFilter.onLoadMore}
            maxRenderedItems={selectedFilter.maxRenderedItems}
          />
        </div>
      )
    case "date":
      return (
        <IntervalPicker
          publishedInterval={selectedFilter.selectedDateRange}
          onChangePublishedInterval={(range) => {
            if (!range) {
              onDeleteFilter()
            } else {
              selectedFilter.onSelectDateRange(range)
            }
          }}
          onPresetSelect={(preset) => {
            const range = newFullIntervalFromTimestampRange(
              preset.toTimestampRangePB()
            )
            if (!range) return
            selectedFilter.onSelectDateRange(range)
            close()
          }}
          presetsToDisplay={selectedFilter.presetsToDisplay}
          disabledMode={selectedFilter.disabledMode}
        />
      )
  }
}
